import { createContext, useCallback, useContext, type ReactNode } from "react";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { isApiError, type ApiClient } from "./client";

const ApiContext = createContext<ApiClient | null>(null);

export function ApiProvider({ client, children }: { client: ApiClient; children: ReactNode }) {
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error("useApi() must be used inside <ApiProvider>");
  return client;
}

/** Like `useApi()` but `null` outside an `<ApiProvider>` (widgets that also render standalone). */
export function useOptionalApi(): ApiClient | null {
  return useContext(ApiContext);
}

/**
 * Turns a thrown value into text for an inline error: the API's own localized message when
 * there is one, otherwise a catalog fallback (network / generic).
 */
/** API error codes that must never reach the screen raw: code → plain-language catalog message. */
export const ERROR_CODE_MESSAGES: Readonly<Record<string, MessageKey>> = {
  APP_INSTALL_NOT_STORE_SOURCED: "dashboard.auth.errorCodes.APP_INSTALL_NOT_STORE_SOURCED",
  AUTH_EMAIL_UNVERIFIED: "dashboard.auth.errorCodes.AUTH_EMAIL_UNVERIFIED",
  CHANNEL_ALREADY_CONNECTED: "dashboard.auth.errorCodes.CHANNEL_ALREADY_CONNECTED",
  N8N_URL_BLOCKED: "integrationsHub.n8n.urlBlocked",
  AI_FLOW_CREDITS: "dashboard.flows.list.aiPatch.errors.credits",
  AI_BUDGET_EXCEEDED: "dashboard.flows.list.aiPatch.errors.credits",
  AI_FLOW_INVALID_OUTPUT: "dashboard.flows.list.aiPatch.errors.invalid",
  AI_FLOW_PAUSED: "dashboard.flows.list.aiPatch.errors.paused",
  AI_FLOW_PROMPT_REJECTED: "dashboard.flows.list.aiPatch.errors.rejected",
  AI_FLOW_REVISION_CONFLICT: "dashboard.flows.list.aiPatch.errors.conflict",
  SEQUENCE_NOT_READY: "dashboard.errors.sequenceNotReadyBody",
  BYOK_INVALID_KEY: "byok.errors.invalidKey",
  BYOK_ALREADY_EXISTS: "byok.errors.alreadyExists",
  BYOK_NOT_FOUND: "byok.errors.notFound",
  BYOK_KEY_REJECTED: "byok.errors.keyRejected",
  WORKSPACE_LOGO_INVALID: "dashboard.settings.workspace.logoInvalidType",
  WORKSPACE_LOGO_TOO_LARGE: "dashboard.settings.workspace.logoTooLarge",
  BYOK_INVALID_MODEL: "byok.errors.invalidModel",
  // OWN-199: catalog errors carry their real reason in the global toast, not "invalid data".
  CATALOG_ONE_PER_NUMBER: "catalog.errors.CATALOG_ONE_PER_NUMBER",
  CATALOG_SCOPE_MISSING: "catalog.errors.CATALOG_SCOPE_MISSING",
  CATALOG_CREATE_DENIED: "catalog.errors.CATALOG_CREATE_DENIED",
  CATALOG_BUSINESS_NOT_FOUND: "catalog.errors.CATALOG_BUSINESS_NOT_FOUND",
  CATALOG_NOT_FOUND_IN_META: "catalog.errors.CATALOG_NOT_FOUND_IN_META",
  CATALOG_CHANNEL_NOT_CAPABLE: "catalog.errors.CATALOG_CHANNEL_NOT_CAPABLE",
  CATALOG_UNAVAILABLE: "catalog.errors.CATALOG_UNAVAILABLE",
  CATALOG_META_ERROR: "catalog.errors.CATALOG_META_ERROR",
  CATALOG_MODE_NOT_SYNCED: "catalog.errors.CATALOG_MODE_NOT_SYNCED",
  CATALOG_CURRENCY_MISMATCH: "catalog.errors.CATALOG_CURRENCY_MISMATCH",
  CATALOG_VALIDATION: "catalog.errors.CATALOG_VALIDATION",
};

const ARABIC = /[\u0600-\u06FF]/;

/** FB3-47: status → catalog message, so a failed call never ends in silence or raw English. */
function statusMessageKey(status: number): MessageKey | null {
  if (status === 401) return "dashboard.errors.sessionExpiredBody";
  if (status === 402) return "dashboard.errors.notInPlanBody";
  if (status === 403) return "dashboard.errors.forbiddenBody";
  if (status === 404) return "dashboard.errors.notFoundBody";
  if (status === 409) return "dashboard.errors.conflictBody";
  if (status === 429) return "dashboard.errors.rateLimitedBodyNoWait";
  if (status === 400 || status === 422) return "dashboard.errors.invalidBody";
  if (status >= 500) return "dashboard.errors.serverBody";
  return null;
}

/**
 * Turns a thrown value into text for an inline error or toast, always in the UI language:
 * a mapped code, then the server's own message only when it is readable in that language,
 * then a message for the HTTP status, then the generic fallback. Never a raw code.
 */
export function useErrorMessage(): (error: unknown) => string {
  const { t, locale } = useI18n();
  // Stable identity: callers list this in useCallback/useEffect deps, so a new function per
  // render would re-run their effects forever (UX-01).
  return useCallback(
    (error) => {
      if (isApiError(error)) {
        if (error.status === 0) return t("dashboard.auth.networkError");
        const mapped = ERROR_CODE_MESSAGES[error.code];
        if (mapped) return t(mapped);
        const readable =
          error.message !== "" &&
          !/^[A-Z0-9_.]+$/.test(error.message) &&
          ARABIC.test(error.message) === (locale === "ar");
        if (readable) return error.message;
        const key = statusMessageKey(error.status);
        if (key) return t(key);
      }
      const fallback: MessageKey = "dashboard.auth.genericError";
      return t(fallback);
    },
    [t, locale],
  );
}
